import { Navigate, Route, Routes } from 'react-router-dom';

import {
  RequireAuth,
  RequireClinicAdmin,
  RequireClinicSubscriptionActive,
  RequireGuest,
  RequireSuperadmin,
} from './features/auth/index.js';
import { AuthCallbackPage } from './routes/auth/callback.js';
import { LoginPage } from './routes/auth/login.js';
import { ResetPasswordPage } from './routes/auth/reset.js';
import { ResetPasswordConfirmPage } from './routes/auth/reset-confirm.js';
import { SignUpPage } from './routes/auth/signup.js';
import { DashboardPage } from './routes/dashboard/index.js';
import { ClinicAiSettingsPage } from './routes/clinic/settings/ai/index.js';
import { ClinicAppearancePage } from './routes/clinic/appearance/index.js';
import { ClinicPlanPage } from './routes/clinic/plan/index.js';
import { ClinicProfilePage } from './routes/clinic/profile/index.js';
import { ClinicTeamPage } from './routes/clinic/team/index.js';
import { PaymentPendingPage } from './routes/clinic/billing/PaymentPendingPage.js';
import { SuspendedPage } from './routes/clinic/billing/SuspendedPage.js';
import { WelcomePage } from './routes/clinic/billing/WelcomePage.js';
import { SuperadminAiDashboardPage } from './routes/superadmin/dashboard/index.js';
import { DashboardEditorPage, DashboardsListPage } from './routes/superadmin/dashboards/index.js';
import { ClinicDetailPage, ClinicsListPage } from './routes/superadmin/clinics/index.js';
import { LeadDetailPage, LeadsListPage } from './routes/superadmin/leads/index.js';
import { SuperadminAiSettingsPage } from './routes/superadmin/settings/ai/index.js';
import { SuperadminPlatformSettingsPage } from './routes/superadmin/settings/platform/index.js';
import { SuperadminPlansPage } from './routes/superadmin/billing/plans/index.js';
import { OnboardingFormPage } from './routes/onboarding/index.js';
import { PublicProposalPage } from './routes/proposta/index.js';
import { DevShowcasePage } from './routes/_dev/showcase.js';
import { NotFoundPage } from './routes/_dev/not-found.js';
import { featureFlags } from './lib/featureFlags.js';

export function App() {
  return (
    <Routes>
      {/* Raiz: dashboard (o guard encaminha para /login se não autenticado) */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* Callback do Supabase (reset de senha, confirm de email, magic link). */}
      {/* Não entra em guard — precisa rodar com ou sem sessão prévia. */}
      <Route path="/auth/callback" element={<AuthCallbackPage />} />

      {/* Onboarding público — lead acessa via token sem login. */}
      {/* Handled pela edge function `read-onboarding-submission`. */}
      <Route path="/onboarding/:token" element={<OnboardingFormPage />} />

      {/* Proposta comercial pública — lead acessa via token sem login. */}
      {/* Handled pela edge function `read-public-proposal`. */}
      <Route path="/proposta/:token" element={<PublicProposalPage />} />

      {/* Aterrissagem do magic link de invite/ativação. PÚBLICA porque
          chega com `#access_token=...` no fragment (gerado por
          auth.admin.generateLink no edge function). A própria página
          processa o fragment via supabase.auth.setSession e depois
          aceita o invite. Fora de RequireAuth pra evitar redirect pra
          /login antes do supabase-js processar o token. */}
      <Route path="/welcome" element={<WelcomePage />} />

      {/* Rotas só-para-visitantes */}
      <Route element={<RequireGuest />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset" element={<ResetPasswordPage />} />
        {featureFlags.enableSignup ? <Route path="/signup" element={<SignUpPage />} /> : null}
      </Route>

      {/* Rotas protegidas */}
      <Route element={<RequireAuth />}>
        <Route path="/reset/confirm" element={<ResetPasswordConfirmPage />} />

        {/* Páginas do gate de pagamento (Modelo C híbrido — ADR 0009).
            Ficam DENTRO de RequireAuth (precisam sessão pra mostrar
            contexto da clinic) mas FORA de RequireClinicSubscriptionActive
            — caso contrário o gate redirecionaria pra elas mesmas em
            loop infinito. */}
        <Route path="/clinic/payment-pending" element={<PaymentPendingPage />} />
        <Route path="/clinic/suspended" element={<SuspendedPage />} />

        {/* Perfil cadastral da clínica — fora do gate de pagamento porque
            owner em payment-pending ainda precisa atualizar dados (corrigir
            email pra receber fatura, mudar telefone, etc). Acesso restrito
            a clinic_owner / clinic_admin via RequireClinicAdmin.
            Plano + Pagamentos do owner — também fora do gate, porque owner
            em payment-pending precisa especialmente desta tela pra ver o
            que pagar. */}
        <Route element={<RequireClinicAdmin />}>
          <Route path="/perfil" element={<ClinicProfilePage />} />
          <Route path="/plano" element={<ClinicPlanPage />} />
        </Route>

        {/* /welcome MOVIDA PRA FORA do RequireAuth — agora é rota
            pública. Veja comentário no bloco público acima. */}

        {/* Rotas exclusivas do Superadmin (claim `is_superadmin` no JWT).
            Não passam pelo gate de subscription porque superadmin tem
            fast-path em resolveClinicPaymentGate (Karla acessa
            cross-clinic mesmo se a subscription dela está incomplete). */}
        <Route element={<RequireSuperadmin />}>
          <Route path="/superadmin/dashboard" element={<SuperadminAiDashboardPage />} />
          <Route path="/superadmin/dashboards" element={<DashboardsListPage />} />
          <Route path="/superadmin/dashboards/:id" element={<DashboardEditorPage />} />
          <Route path="/superadmin/leads" element={<LeadsListPage />} />
          <Route path="/superadmin/leads/:id" element={<LeadDetailPage />} />
          <Route path="/superadmin/clinics" element={<ClinicsListPage />} />
          <Route path="/superadmin/clinics/:id" element={<ClinicDetailPage />} />
          <Route path="/superadmin/settings/ai" element={<SuperadminAiSettingsPage />} />
          <Route
            path="/superadmin/settings/platform"
            element={<SuperadminPlatformSettingsPage />}
          />
          <Route path="/superadmin/billing/plans" element={<SuperadminPlansPage />} />
        </Route>

        {/* Rotas operacionais da clínica — atrás do gate de pagamento
            (Modelo C híbrido — ADR 0009). Quando a subscription está
            'incomplete', o guard redireciona pra /clinic/payment-pending. */}
        <Route element={<RequireClinicSubscriptionActive />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route element={<RequireClinicAdmin />}>
            <Route path="/settings/ai" element={<ClinicAiSettingsPage />} />
            <Route path="/equipe" element={<ClinicTeamPage />} />
            <Route path="/identidade-visual" element={<ClinicAppearancePage />} />
          </Route>
        </Route>
      </Route>

      {/* Dev-only: showcase permanece público */}
      <Route path="/_dev/showcase" element={<DevShowcasePage />} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
